'use client';

import React from 'react';
import { OTPInput } from 'input-otp';
import InputUi from 'src/components/primitives/Input/InputUi';
import { cj } from 'src/utils/classNames';
import { Email } from '@drift-labs/icons';
import TextButton from 'src/components/primitives/TextButton/TextButton';
import usePrivyEmailOtpPage from 'src/components/Modals/Onboarding/ConnectWalletModal/PrivyEmailOtpPage/hooks/usePrivyEmailOtpPage';

interface Props {
	email: string;
	resendSecondsRemaining: number;
	setIsOtpSent: (isOtpSent: boolean) => void;
}

export default function PrivyEmailOtpPage({
	email,
	setIsOtpSent,
	resendSecondsRemaining,
}: Props) {
	const {
		code,
		setCode,
		handleSendCode,
		handleLoginWithCode,
		isVerifyingCode,
		isSendingCode,
		isResendButtonDisabled,
	} = usePrivyEmailOtpPage({ email, resendSecondsRemaining, setIsOtpSent });

	return (
		<div className="flex flex-col items-center gap-y-md">
			<div className="flex flex-col items-center gap-y-sm text-text-default">
				<Email size={40} />
				<h3 className="typo-t2">Enter Code</h3>
			</div>

			<div className="flex flex-col items-center gap-y-2xl">
				<p className="text-center">
					Please check {email} for an email from privy.io and enter the code
					below.
				</p>

				<OTPInput
					maxLength={6}
					onComplete={handleLoginWithCode}
					value={code}
					onChange={setCode}
					containerClassName="flex items-center gap-x-sm"
					disabled={isVerifyingCode}
					render={({ slots }) =>
						slots.map((slot, idx) => (
							<InputUi.Container
								key={idx}
								className={cj(
									'relative size-10 text-center',
									!isVerifyingCode && slot.isActive && 'border-purple-link',
									isVerifyingCode && 'animate-pulse'
								)}
								disabled={isVerifyingCode}
							>
								<InputUi.Value size="md" disabled={isVerifyingCode}>
									{slot.char}
								</InputUi.Value>

								{slot.hasFakeCaret && (
									<div className="pointer-events-none absolute inset-0 flex items-center justify-center">
										<div className="h-lg w-px animate-caret-blink bg-text-default" />
									</div>
								)}
							</InputUi.Container>
						))
					}
				/>

				<p className="flex items-center gap-x-xs text-center">
					Didn&apos;t get an email?{' '}
					<TextButton
						size="sm"
						variant="tertiary"
						onClick={handleSendCode}
						disabled={isResendButtonDisabled}
						isLoading={isSendingCode}
					>
						Resend {isResendButtonDisabled && `(${resendSecondsRemaining}s)`}
					</TextButton>
				</p>
			</div>
		</div>
	);
}
